// VcaContactSkippedPanel — chunk SLAV (Skip-Log Admin Viewer), 2026-04-30.
//
// Renders inside the Admin VCA Data tab, BELOW the SyncAnomaliesPanel.
// Lists rows from /admin/vca-contact-skipped (default unreviewed filter),
// gives per-row triage with three outcomes: ignored / company_created /
// false_positive.  v1 does NOT actually re-add false-positives to contacts;
// the mark exists so we can find them later via SQL.
//
// All gating server-side via Cap.MANAGE_VCA_SYNC.  Parent only renders this
// when canVca is true.

import React, { useEffect, useState } from "react";
import { apiFetch } from "../api";
import useCollapse from "./useCollapse";

const OUTCOME_OPTIONS = [
  { id: "ignored",         label: "Ignore",
    desc: "Not actually relevant; leave skipped",
    color: "#6b7280" },
  { id: "company_created", label: "Company created",
    desc: "Manually created a company record for this entity",
    color: "#10b981" },
  { id: "false_positive",  label: "False positive",
    desc: "Should NOT have been skipped — flag for re-add (v1 does not re-add automatically)",
    color: "#f59e0b" },
];

const REASON_STYLE = {
  org_signal:             { color: "#dc2626", label: "Org signal" },
  org_signal_retroactive: { color: "#9333ea", label: "Org signal (retroactive)" },
};

function fmtTs(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return s;
  }
}

function reasonSplit(skipReason) {
  if (!skipReason) return { category: "unknown", detail: "" };
  const idx = skipReason.indexOf(":");
  if (idx === -1) return { category: skipReason, detail: "" };
  return {
    category: skipReason.slice(0, idx),
    detail: skipReason.slice(idx + 1),
  };
}

export default function VcaContactSkippedPanel({ onChanged }) {
  const [filter, setFilter] = useState("false");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [reviewState, setReviewState] = useState({});
  const [expanded, setExpanded] = useState({});
  const { open, toggle, chevron } = useCollapse(false); // collapsed by default — triage-on-demand

  const load = () => {
    setLoading(true);
    setErr("");
    apiFetch(`/admin/vca-contact-skipped?reviewed=${filter}&limit=200`)
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => setErr(String(e?.message || e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const startReview = (id, outcomeId) =>
    setReviewState((s) => ({
      ...s,
      [id]: { open: true, outcome: outcomeId, note: "", busy: false },
    }));

  const cancelReview = (id) =>
    setReviewState((s) => ({ ...s, [id]: { ...s[id], open: false } }));

  const setReviewNote = (id, note) =>
    setReviewState((s) => ({ ...s, [id]: { ...s[id], note } }));

  const submitReview = (id) => {
    const rs = reviewState[id];
    if (!rs?.outcome) return;
    setReviewState((s) => ({ ...s, [id]: { ...s[id], busy: true } }));
    apiFetch(`/admin/vca-contact-skipped/${id}/review`, {
      method: "POST",
      body: { outcome: rs.outcome, note: rs.note || null },
    })
      .then(() => {
        load();
        onChanged?.();
      })
      .catch((e) => setErr(String(e?.message || e)))
      .finally(() =>
        setReviewState((s) => ({
          ...s,
          [id]: { open: false, outcome: null, note: "", busy: false },
        })),
      );
  };

  const toggleExpand = (id) =>
    setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const unreviewedCount = filter === "false" ? rows.length : null;

  return (
    <div className="card" style={{ padding: 16, marginBottom: 16 }}>
      <div className="section-header" onClick={toggle} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
        <span className="section-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {chevron}
          Skipped VCA Contacts
          {unreviewedCount > 0 && (
            <span style={{
              padding: "2px 8px", borderRadius: 10, background: "#9333ea",
              color: "#fff", fontSize: 11, fontWeight: 600,
            }}>
              {unreviewedCount} unreviewed
            </span>
          )}
          {unreviewedCount === 0 && (
            <span style={{ fontSize: 12, color: "var(--text3)", fontWeight: 400 }}>
              — all reviewed
            </span>
          )}
        </span>
        {open && (
        <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="form-control"
            style={{ width: 180, fontSize: 12 }}
          >
            <option value="false">Unreviewed</option>
            <option value="true">Reviewed only</option>
            <option value="all">All</option>
          </select>
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            Refresh
          </button>
        </div>
        )}
      </div>
      {open && (<>

      <div style={{ marginTop: 8, fontSize: 12, color: "var(--text3)" }}>
        VCA contact payloads where the org-detection guard caught an organization name (Hospital,
        LLC, Inc., etc.) and skipped the contacts INSERT.  Triage each: <i>Ignore</i> if not
        relevant, <i>Company created</i> if you've added a company record manually, or
        <i> False positive</i> if it's actually a person — flagged for later re-add.
      </div>

      {err && <div className="alert-error" style={{ marginTop: 10 }}>{err}</div>}

      {loading ? (
        <div className="loading" style={{ marginTop: 12 }}>Loading skip log...</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 16, color: "var(--text3)", fontSize: 13, textAlign: "center" }}>
          {filter === "false"
            ? "✓ No unreviewed skip-log rows."
            : filter === "true"
            ? "No reviewed rows in the last 200."
            : "Skip log is empty."}
        </div>
      ) : (
        <div style={{ marginTop: 10, overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)", textAlign: "left", color: "var(--text3)" }}>
                <th style={{ padding: "6px 8px" }}>Detected</th>
                <th style={{ padding: "6px 8px" }}>Reason</th>
                <th style={{ padding: "6px 8px" }}>Name (raw)</th>
                <th style={{ padding: "6px 8px" }}>Email</th>
                <th style={{ padding: "6px 8px" }}>Phone</th>
                <th style={{ padding: "6px 8px" }}>Status</th>
                <th style={{ padding: "6px 8px" }}>{/* actions */}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const rs = reviewState[r.id] || {};
                const reason = reasonSplit(r.skip_reason);
                const reasonStyle = REASON_STYLE[reason.category] || { color: "var(--text2)", label: reason.category };
                const outcomeMeta = OUTCOME_OPTIONS.find(o => o.id === r.review_outcome);
                const isExpanded = !!expanded[r.id];
                return (
                  <React.Fragment key={r.id}>
                    <tr style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>{fmtTs(r.detected_at)}</td>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                        <span style={{ color: reasonStyle.color, fontWeight: 600 }}>{reasonStyle.label}</span>
                        {reason.detail && (
                          <span style={{ color: "var(--text3)", marginLeft: 4, fontFamily: "monospace" }}>
                            ({reason.detail})
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "6px 8px", maxWidth: 360 }}>
                        <button
                          onClick={() => toggleExpand(r.id)}
                          style={{
                            background: "none", border: "none", padding: 0, cursor: "pointer",
                            color: "var(--text1)", textAlign: "left", fontFamily: "inherit",
                            fontSize: "inherit",
                          }}
                          title={isExpanded ? "Hide raw payload" : "Show raw payload"}
                        >
                          {isExpanded ? "▾ " : "▸ "}{r.raw_name}
                        </button>
                        {r.raw_company && (
                          <div style={{ fontSize: 11, color: "var(--text3)" }}>
                            company: {r.raw_company}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "6px 8px", color: "var(--text2)" }}>{r.raw_email || "—"}</td>
                      <td style={{ padding: "6px 8px", color: "var(--text2)" }}>{r.raw_phone || "—"}</td>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                        {r.reviewed_at ? (
                          <span title={`by ${r.reviewed_by_username || "?"}`}>
                            <span style={{
                              color: outcomeMeta?.color || "var(--text3)",
                              fontWeight: 600,
                            }}>
                              ✓ {outcomeMeta?.label || r.review_outcome}
                            </span>
                            <div style={{ fontSize: 10, color: "var(--text3)" }}>
                              {fmtTs(r.reviewed_at)}
                            </div>
                          </span>
                        ) : (
                          <span style={{ color: "#9333ea", fontWeight: 600 }}>Open</span>
                        )}
                      </td>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                        {!r.reviewed_at && !rs.open && (
                          <div style={{ display: "flex", gap: 4 }}>
                            {OUTCOME_OPTIONS.map((o) => (
                              <button
                                key={o.id}
                                className="btn btn-outline btn-sm"
                                onClick={() => startReview(r.id, o.id)}
                                title={o.desc}
                                style={{ borderColor: o.color, color: o.color }}
                              >
                                {o.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>

                    {isExpanded && r.raw_payload && (
                      <tr style={{ borderBottom: "1px solid var(--border)", background: "var(--bg2)" }}>
                        <td colSpan={7} style={{ padding: "8px 16px" }}>
                          <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 4 }}>Raw payload (from VCA):</div>
                          <pre style={{
                            margin: 0, padding: 8, fontSize: 11,
                            background: "var(--bg)", border: "1px solid var(--border)",
                            borderRadius: 4, maxHeight: 200, overflow: "auto",
                            whiteSpace: "pre-wrap", wordBreak: "break-word",
                          }}>
                            {JSON.stringify(r.raw_payload, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}

                    {rs.open && (
                      <tr style={{ borderBottom: "1px solid var(--border)", background: "var(--bg2)" }}>
                        <td colSpan={7} style={{ padding: "8px 12px" }}>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <span style={{ fontSize: 12, color: "var(--text2)", whiteSpace: "nowrap" }}>
                              Marking as <b style={{ color: OUTCOME_OPTIONS.find(o => o.id === rs.outcome)?.color }}>
                                {OUTCOME_OPTIONS.find(o => o.id === rs.outcome)?.label}
                              </b>:
                            </span>
                            <input
                              type="text"
                              value={rs.note || ""}
                              onChange={(e) => setReviewNote(r.id, e.target.value)}
                              placeholder="Optional note (context for future reference)"
                              className="form-control"
                              style={{ flex: 1, fontSize: 12 }}
                              disabled={rs.busy}
                            />
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => submitReview(r.id)}
                              disabled={rs.busy}
                            >
                              {rs.busy ? "Saving..." : "Confirm"}
                            </button>
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => cancelReview(r.id)}
                              disabled={rs.busy}
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </>)}
    </div>
  );
}
